import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { UX_THEME_V5_ENABLED } from '../config/themeAccess';

/**
 * ThemeContext — M-D5 Welle 3 (`plan-ux-d5-w3-light-flip.md` §2).
 *
 * SSOT für das aktive Theme bleibt das `data-theme`-Attribut auf `<html>`
 * (bereits VOR dem React-Mount vom `public/index.html` (FOUC-Init-Script)
 * gesetzt: gespeicherte Wahl > OS-Präferenz (flag-gated) > dark). Dieser
 * Context liest den Attribut-Wert nur EINMAL beim Mount (Init-State) und
 * hält ihn danach als React-State synchron zu jedem `setTheme()`-Aufruf —
 * es gibt keinen zweiten, konkurrierenden Schreibpfad auf `data-theme`.
 *
 * `setTheme()` ist die EINZIGE Stelle, die `data-theme` ändert UND
 * `localStorage['fb-theme']` persistiert (Schlüssel exakt wie in
 * `public/index.html` (FOUC-Init-Script), damit das Init-Script beim
 * nächsten Hard-Reload denselben Wert liest — kein FOUC).
 *
 * @module context/ThemeContext
 */

/** Die beiden unterstützten Themes. Light ist inert, bis W3-5 den Default flippt. */
export type Theme = 'dark' | 'light';

/** localStorage-Schlüssel — MUSS mit `public/index.html` (FOUC-Init-Script) übereinstimmen. */
const THEME_STORAGE_KEY = 'fb-theme';

/**
 * Whitelist-Mapper (fail-closed, §2): jeder Wert außer exakt `'light'` wird
 * als `'dark'` behandelt. Deckt sowohl einen unbekannten/korrupten
 * localStorage-Wert als auch ein fehlendes/leeres `data-theme`-Attribut
 * (z. B. in Tests, die `render()` ohne vorheriges Init-Script aufrufen) ab.
 */
function toValidTheme(value: string | null | undefined): Theme {
  return value === 'light' ? 'light' : 'dark';
}

interface ThemeContextValue {
  theme: Theme;
  /**
   * Setzt das aktive Theme: aktualisiert `document.documentElement.dataset.theme`,
   * persistiert nach `localStorage['fb-theme']` und aktualisiert den React-State.
   * Ein unbekannter Theme-Wert wird über {@link toValidTheme} fail-closed auf
   * `'dark'` gemappt statt roh übernommen zu werden.
   */
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Liest den initialen Theme-Wert aus dem bereits vom Init-Script gesetzten
 * `data-theme`-Attribut. Fällt fail-closed auf `'dark'` zurück, falls das
 * Attribut (noch) fehlt — z. B. in Testumgebungen ohne `public/index.html`.
 */
function readInitialTheme(): Theme {
  if (typeof document === 'undefined') {
    return 'dark';
  }
  return toValidTheme(document.documentElement.dataset.theme);
}

/**
 * Provider für {@link useTheme}. Wird in `App.tsx` um die gesamte App gelegt,
 * damit sowohl der TopBar-Umschalter (`MainLayout`) als auch die
 * AccountPage-Sektion "Darstellung" denselben State teilen.
 */
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  const setTheme = useCallback((next: Theme) => {
    const validated = toValidTheme(next);
    document.documentElement.dataset.theme = validated;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, validated);
    } catch (error) {
      // localStorage kann in restriktiven Umgebungen (Privacy-/Incognito-Mode,
      // Storage-Quota) werfen. Das Attribut ist bereits gesetzt — ein
      // Persistenz-Fehler darf den sichtbaren Theme-Wechsel selbst nicht
      // verhindern, geht aber nicht still unter (fail-soft NUR hier, das
      // sichtbare Verhalten in der laufenden Session bleibt korrekt; die
      // Präferenz überlebt in diesem Fall nur keinen Reload).
      // eslint-disable-next-line no-console
      console.error('ThemeContext: localStorage-Persistenz fehlgeschlagen', error);
    }
    setThemeState(validated);
  }, []);

  // WP-L1 / qa-ux-theme-v5-light.md §5.3 — Rollback-Self-Heal (fail-closed-
  // symmetrisch). `localStorage['fb-theme']` überlebt den Flag-Rückzug, und das
  // `public/index.html` (FOUC-Init-Script) liest die gespeicherte Wahl
  // FLAG-UNABHÄNGIG (nur der OS-Fallback ist flag-gated). Ohne diesen
  // Guard bliebe jede Nutzerin, die während der Flag-on-Phase auf Light gestellt
  // hat, nach dem Flag-Rückzug dauerhaft im Light-Theme gefangen — ohne UI-Weg
  // zurück (der Umschalter ist dann self-gated wieder weg). Ist das Flag AN,
  // ist der Guard ein No-op (Bedingung false) ⇒ kein Flackern im Normalbetrieb.
  useEffect(() => {
    if (
      !UX_THEME_V5_ENABLED &&
      typeof document !== 'undefined' &&
      document.documentElement.dataset.theme === 'light'
    ) {
      // Nutzt den bestehenden einzigen Schreibpfad: setzt data-theme + localStorage
      // zurück auf 'dark'. Ein einzelner dunkler Frame im Rollback-Fall ist bewusst
      // akzeptiert (§5.3).
      setTheme('dark');
    }
    // setTheme ist via useCallback stabil (leere deps) ⇒ Effekt läuft einmal beim Mount.
  }, [setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Zugriffs-Hook auf {@link ThemeContext}. Wirft außerhalb eines
 * `ThemeProvider` (fail-closed, kein stiller `undefined`-Fallback — jeder
 * Konsument muss innerhalb von `App.tsx`s Provider-Baum liegen).
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme() must be used within a ThemeProvider');
  }
  return ctx;
}
